/**
 * Visible text of an HTML content item — what a learner would actually read.
 *
 * Parsed with DOMParser rather than stripped with regex. Regex stripping of
 * `<head>…</head>` misses a `<title>` that sits outside a head element, which the
 * themed content items do carry: the title text then survived tag-stripping and was
 * counted as words. A parser has no such gap — the browser hoists `<title>` and
 * other metadata into `<head>`, so reading `body.textContent` cannot see it.
 *
 * `<style>` / `<script>` inside the body still contribute text nodes, so those are
 * removed explicitly before reading the text.
 */
export function extractVisibleText(html?: string | null): string {
  if (!html) return '';

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // A <title> is only hoisted into <head> when it appears before any body
    // content. Saved items store a fragment where it can follow the content, and
    // the parser then inserts it into the current node — i.e. the body — so its
    // text lands in body.textContent. Remove these by name wherever they ended up,
    // rather than trusting head/body placement.
    doc
      .querySelectorAll('title, meta, link, style, script, template, noscript')
      .forEach((el) => el.remove());
    return (doc.body?.textContent || '').replace(/\s+/g, ' ').trim();
  } catch {
    // Non-DOM environment (tests, SSR): fall back to the regex approach. Each of
    // these must remove the element's CONTENT too, not just its tags — the general
    // tag strip below would otherwise leave the inner text behind as words, which is
    // the bug this function exists to fix.
    return html
      .replace(/<head[\s\S]*?<\/head>/gi, '')
      .replace(/<title[\s\S]*?<\/title>/gi, '')
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<template[\s\S]*?<\/template>/gi, '')
      .replace(/<noscript[\s\S]*?<\/noscript>/gi, '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/\s+/g, ' ')
      .trim();
  }
}

/** Number of visible words. Empty content is 0, not 1. */
export function countVisibleWords(html?: string | null): number {
  const text = extractVisibleText(html);
  return text ? text.split(/\s+/).filter(Boolean).length : 0;
}

/**
 * Returns true when an HTML content item is *effectively* empty — i.e. it has
 * no visible body text. A freshly-created item is a full themed document whose
 * body is just the starter placeholder, and an item whose body was cleared and
 * saved still carries the theme CSS in <head> — both must count as "empty" so
 * the UI shows Generate (not Regenerate) and offers the Upload option.
 */
export function isHtmlEffectivelyEmpty(html?: string | null): boolean {
  if (!html) return true;
  const text = extractVisibleText(html);
  if (text.length === 0) return true;
  // Starter placeholder from buildThemedStarterHtml.
  if (text.includes('Start writing your content here')) return true;
  return false;
}
